import { useMemo, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ChartTooltip } from "./ChartTooltip";
import { breakReport } from "../lib/breaks";
import { BreakOverruns } from "./BreakOverruns";
import { RepeatOffenders } from "./RepeatOffenders";
import { fmtClock, fmtDur } from "../lib/pickerSheet";
import { monthLabel } from "../lib/csvAnalysis";
import { fmtNum, slug } from "../lib/format";

const clk = (s) => (s == null ? "—" : fmtClock(s));
const dur = (s) => (s == null ? "—" : fmtDur(s));
const trig = "h-9 bg-[#0B0F19] border-slate-700 text-slate-100 font-mono text-xs";
const TH = ({ children }) => <th className="text-left px-3 py-3 font-mono text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap first:pl-5">{children}</th>;

const Row = ({ r, label, onClick, active, testId, allowance }) => (
  <tr data-testid={testId} onClick={onClick} className={`border-b border-slate-800/60 transition-colors ${onClick ? "cursor-pointer hover:bg-slate-800/40" : ""} ${active ? "bg-ep/10" : ""}`}>
    <td className="pl-5 py-2.5 text-slate-100 font-medium whitespace-nowrap">{label}</td>
    <td className="px-3 font-mono text-slate-400">{r.days}</td>
    <td className="px-3 font-mono text-slate-300">{r.breaksPerDay.toFixed(1)}</td>
    <td className="px-3 font-mono text-amber-300">{dur(r.breakPerDay)}</td>
    <td className="px-3 font-mono text-slate-200">{clk(r.lunchStart)}</td>
    <td className="px-3 font-mono text-slate-400 whitespace-nowrap">{clk(r.lunchFrom)}–{clk(r.lunchTo)}</td>
    <td className="px-3 font-mono text-slate-200">{dur(r.lunchAvg)}</td>
    <td className={`px-3 font-mono ${r.overPct > 50 ? "text-rose-300" : "text-slate-300"}`}>{r.overPct == null ? "—" : `${r.over} (${r.overPct.toFixed(0)}%)`}</td>
    <td className="px-3 font-mono text-slate-500">{r.noBreak}</td>
  </tr>
);

export const BreakReport = ({ users, cfg }) => {
  const [picker, setPicker] = useState("all");
  const scoped = picker === "all" ? users : users.filter((u) => u.id === picker);
  const rep = useMemo(() => breakReport(scoped, cfg.breakMin * 60), [scoped, cfg.breakMin]);
  const [month, setMonth] = useState(null);
  const m = rep.months.find((x) => x.key === month) || rep.months[rep.months.length - 1];
  if (!m) return <p className="text-sm text-slate-500">No break data in this range.</p>;
  const hist = m.hours.map((v, h) => ({ label: `${String(h).padStart(2, "0")}:00`, breaks: v }));
  const head = ["Days", "Breaks / day", "Break time / day", "Typical lunch start", "Middle 50% start", "Avg lunch length", `Lunch > ${cfg.breakMin} min`, "Days no break"];
  return (
    <div className="space-y-6" data-testid="break-report">
      <div className="panel px-4 py-3 flex flex-wrap items-center gap-3">
        <span className="label-mono">Picker</span>
        <Select value={picker} onValueChange={setPicker}>
          <SelectTrigger data-testid="break-picker-select" className={`${trig} w-[200px]`}><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-80"><SelectItem value="all">All pickers</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.id}</SelectItem>)}</SelectContent>
        </Select>
        <p className="text-[11px] text-slate-500 lg:ml-auto max-w-xl">A break = any gap between picks of {cfg.gapMin}+ min. The longest break each day is treated as lunch. Times are medians so one odd day doesn't skew them.</p>
      </div>

      {picker === "all" && <BreakOverruns users={users} cfg={cfg} onPicker={setPicker} />}
      {picker === "all" && <RepeatOffenders users={users} cfg={cfg} onPicker={setPicker} />}

      <div className="panel overflow-x-auto" data-testid="break-monthly-table">
        <div className="p-5 border-b border-slate-800"><h3 className="font-display text-lg font-semibold text-slate-100">Monthly break summary · {picker === "all" ? "all pickers" : picker}</h3><p className="text-xs text-slate-500">Click a month to deep dive below</p></div>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-800"><TH>Month</TH>{head.map((h) => <TH key={h}>{h}</TH>)}</tr></thead>
          <tbody>{rep.months.map((r) => <Row key={r.key} r={r} label={monthLabel(r.key)} active={r.key === m.key} onClick={() => setMonth(r.key)} testId={`break-month-${r.key}`} />)}</tbody>
        </table>
      </div>

      <div className="panel p-5 sm:p-6" data-testid="break-hour-chart">
        <h3 className="font-display text-lg font-semibold text-slate-100" data-testid="break-deepdive-title">{monthLabel(m.key)} · when breaks start</h3>
        <p className="text-xs text-slate-500 mb-4">Number of breaks starting in each hour · typical lunch {clk(m.lunchStart)} for {dur(m.lunchAvg)}</p>
        <ResponsiveContainer width="100%" height={260} initialDimension={{ width: 900, height: 260 }}>
          <BarChart data={hist} margin={{ top: 22, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#1F2937" vertical={false} />
            <XAxis dataKey="label" stroke="#64748B" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} tickLine={false} interval={1} />
            <YAxis stroke="#64748B" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={40} />
            <Tooltip content={<ChartTooltip />} cursor={{ fill: "#1F2937", opacity: 0.4 }} />
            {m.lunchStart != null && <ReferenceLine x={`${String(Math.floor(m.lunchStart / 3600)).padStart(2, "0")}:00`} stroke="#F59E0B" strokeDasharray="4 3" label={{ value: "typical lunch", fill: "#F59E0B", fontSize: 10, position: "top" }} />}
            <Bar dataKey="breaks" name="Breaks started" fill="#8B5CF6" radius={[3, 3, 0, 0]} maxBarSize={30} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {picker === "all" && (
        <div className="panel overflow-x-auto" data-testid="break-picker-table">
          <div className="p-5 border-b border-slate-800"><h3 className="font-display text-lg font-semibold text-slate-100">{monthLabel(m.key)} · by picker</h3><p className="text-xs text-slate-500">Most break time per day first · click to filter to that picker</p></div>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-800"><TH>Picker</TH>{head.map((h) => <TH key={h}>{h}</TH>)}</tr></thead>
            <tbody>{rep.pickers[m.key].map((r) => <Row key={r.id} r={r} label={r.id} onClick={() => setPicker(r.id)} testId={`break-picker-${slug(r.id)}`} />)}</tbody>
          </table>
        </div>
      )}
      <p className="text-[11px] text-slate-500">{fmtNum(rep.months.reduce((s, r) => s + r.days, 0))} picker-days analysed.</p>
    </div>
  );
};
